import { useEffect, useState } from 'react';
import { INSERT_TOKEN } from '../../constants/insertToken';
import { trackMarketingEvent } from '../../services/marketingAnalytics';
import styles from '../../styles/components/EmptyState.module.css';

const ROUND_SECONDS = 15;
const GRID_CELLS = 9;

type GameStatus = 'idle' | 'playing' | 'finished';

export function parseChallengeScore(search: string): number | null {
  const params = new URLSearchParams(search);
  const values = params.getAll('challenge');
  if (values.length !== 1 || !/^\d{1,3}$/.test(values[0] ?? '')) return null;
  const score = Number(values[0]);
  return Number.isSafeInteger(score) && score > 0 && score <= 999 ? score : null;
}

function nextTarget(previous: number): number {
  // Always move to a different cell so every successful hit has visible feedback.
  return (previous + 1 + Math.floor(Math.random() * (GRID_CELLS - 1))) % GRID_CELLS;
}

export function DubyaDropDemo() {
  const [status, setStatus] = useState<GameStatus>('idle');
  const [interest, setInterest] = useState<'idle' | 'yes' | 'not_yet'>('idle');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const [target, setTarget] = useState(4);
  const challengeScore =
    typeof window === 'undefined' ? null : parseChallengeScore(window.location.search);

  useEffect(() => {
    if (status !== 'playing') return;

    const timer = window.setInterval(() => {
      setTimeLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          setStatus('finished');
          return 0;
        }
        return current - 1;
      });
    }, 1_000);

    return () => window.clearInterval(timer);
  }, [status]);

  const startGame = () => {
    setScore(0);
    setTimeLeft(ROUND_SECONDS);
    setTarget(Math.floor(Math.random() * GRID_CELLS));
    setCopyStatus('idle');
    setStatus('playing');
    trackMarketingEvent('free_game_open');
  };

  const copyChallenge = async () => {
    try {
      const challengeUrl = new URL(INSERT_TOKEN.challengeUrl);
      challengeUrl.searchParams.set('challenge', String(Math.min(score, 999)));
      await navigator.clipboard.writeText(challengeUrl.toString());
      setCopyStatus('copied');
      trackMarketingEvent('copy_challenge_click');
    } catch {
      setCopyStatus('error');
    }
  };

  const collectToken = () => {
    if (status !== 'playing') return;
    setScore((current) => {
      const nextScore = current + 1;
      setBestScore((currentBest) => Math.max(currentBest, nextScore));
      return nextScore;
    });
    setTarget((current) => nextTarget(current));
  };

  const recordInterest = (answer: 'yes' | 'not_yet') => {
    if (interest !== 'idle') return;
    setInterest(answer);
    trackMarketingEvent(
      answer === 'yes' ? 'insert_interest_yes' : 'insert_interest_not_yet'
    );
  };

  return (
    <section className={styles.arcadeDemo} aria-labelledby="insert-drop-title">
      <div className={styles.arcadeDemoHeader}>
        <div>
          <span className={styles.arcadeDemoEyebrow}>PLAYER 1 · FREE DEMO</span>
          <h5 id="insert-drop-title">INSERT DROP</h5>
        </div>
        <div className={styles.arcadeHud} aria-live="polite">
          <span>SCORE <strong>{score}</strong></span>
          <span>HIGH SCORE <strong>{bestScore}</strong></span>
          <span>TIME <strong>{timeLeft}</strong></span>
        </div>
      </div>

      <p>Catch the moving arcade token before the 15-second round ends.</p>

      {status === 'playing' ? (
        <div className={styles.arcadeGrid} aria-label="INSERT Drop game board">
          {Array.from({ length: GRID_CELLS }, (_, index) => {
            const isTarget = index === target;
            return (
              <button
                key={index}
                type="button"
                className={`${styles.arcadeCell} ${isTarget ? styles.arcadeTarget : ''}`}
                aria-label={isTarget ? 'Collect INSERT arcade token' : `Empty arcade slot ${index + 1}`}
                disabled={!isTarget}
                onClick={isTarget ? collectToken : undefined}
              >
                {isTarget ? 'W' : ''}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={styles.arcadeReady}>
          <strong>
            {status === 'finished'
              ? challengeScore === null
                ? `FINAL SCORE · ${score}`
                : score > challengeScore
                  ? `CHALLENGE CLEARED · ${score}`
                  : `BEAT ${challengeScore} · SCORE ${score}`
              : challengeScore === null
                ? 'SYSTEM READY'
                : `CHALLENGE · BEAT ${challengeScore}`}
          </strong>
          <div className={styles.arcadeFinishedActions}>
            <button type="button" onClick={startGame}>
              {status === 'finished' ? 'PLAY AGAIN' : 'START FREE DEMO'}
            </button>
            {status === 'finished' && (
              <>
                <button
                  type="button"
                  className={styles.arcadeSecondaryButton}
                  onClick={copyChallenge}
                  aria-label="Copy INSERT Drop challenge link"
                >
                  {copyStatus === 'copied'
                    ? 'CHALLENGE COPIED'
                    : copyStatus === 'error'
                      ? 'COPY UNAVAILABLE'
                      : 'COPY CHALLENGE'}
                </button>
                <a
                  href="/?launch=insert"
                  onClick={() => trackMarketingEvent('insert_plan_click')}
                >
                  VIEW NEW TOKEN PLAN
                </a>
              </>
            )}
          </div>
          {status === 'finished' && (
            <>
              <span className={styles.arcadeMarketNote}>
                Vidya access: ~$10 in ${INSERT_TOKEN.symbol} → about 30 days.
                USD reference; the token amount will use a short-lived server quote
                against USDC. Free demo · no payment · no token reward.
              </span>
              <div className={styles.arcadeInterest} aria-live="polite">
                {interest === 'idle' ? (
                  <>
                    <span>WOULD YOU USE ${INSERT_TOKEN.symbol} FOR MORE ARCADE TIME?</span>
                    <div
                      className={styles.arcadeInterestActions}
                      role="group"
                      aria-label="INSERT arcade access interest"
                    >
                      <button type="button" onClick={() => recordInterest('yes')}>
                        YES
                      </button>
                      <button type="button" onClick={() => recordInterest('not_yet')}>
                        NOT YET
                      </button>
                    </div>
                  </>
                ) : (
                  <strong>
                    {interest === 'yes'
                      ? 'INTEREST SAVED · TOKEN UTILITY'
                      : 'THANKS · THE FREE DEMO STAYS OPEN'}
                  </strong>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <small>
        Original browser mini-game · no wallet · no payment · no token reward.
      </small>
    </section>
  );
}

export default DubyaDropDemo;
